// /agents/:agentId — ONE project agent, as a page.
//
// Until 2026-09-12 an agent's configuration — its persona, its tools, the
// model it runs on, whether it acts inside workflows — lived only in the
// `cortex.agent.<name>.manifest` endpoint on the project's Flow fork, and the
// Agents page opened the CHAT on click. This page reads the live manifest
// (GET /api/agents/:id/config) and shows it in plain sections; an owner or org
// admin edits the DATA fields — identity, persona, model — and the change is
// written back to the manifest endpoint (PUT …/config), so the fork stays the
// source of truth and the orchestrator picks it up on its next discovery.
// Tools and the actor block are code-shaped and stay read-only here (the
// fleet builds them through "Build a change"). "Chat with agent" opens the
// project's orchestrator chat pinned to this agent — the same event the
// Agents row's Ask action fires.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, MessageSquare, Pencil, Save, X, Loader2, Globe, Lock, Users, AppWindow, Box,
  Wrench, Cpu, Workflow, ShieldCheck, Route, AlertTriangle, RefreshCw, ShieldAlert,
} from "lucide-react";
import { getAgentConfig, updateAgentConfig, updateAgentVisibility, getAgentGuardrailEvents } from "../services/apps";
import { createPageUrl } from "@/utils";
import { useProject } from "@/contexts/ProjectContext";
import { isPortal } from "../utils/portal";
import AgentIcon, { emojiOf } from "../components/AgentIcon";

const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50 disabled:cursor-not-allowed";
const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";
const fieldCls = "w-full rounded-md border border-card-border bg-card-bg text-page-text text-sm px-2.5 py-1.5 outline-none focus:border-nav-active";
const sectionCls = "rounded-lg border border-card-border bg-card-bg p-4 grid gap-3";

// GUARDRAILS — the agent's correctness contract, edited as plain fields and
// sent as one `guardrails` object (shared/agent-guardrails.js validates it on
// the backend with the same rule the runtime enforces). The form holds the
// user-facing shape; guardPatch() turns it back into the manifest block.
const EMPTY_GUARD = { scope: "", grounding: "none", verify: false, on_fail: "refuse", refusal: "", max_tool_calls: "" };
const guardDraftFrom = (g) => ({
  scope: g?.scope || "",
  grounding: g?.grounding || "none",
  verify: !!g?.verify,
  on_fail: g?.on_fail || "refuse",
  refusal: g?.refusal || "",
  max_tool_calls: g?.max_tool_calls != null ? String(g.max_tool_calls) : "",
});
const guardPatch = (d) => {
  const out = {};
  if (d.scope.trim()) out.scope = d.scope.trim();
  if (d.grounding && d.grounding !== "none") out.grounding = d.grounding;
  if (d.verify) out.verify = true;
  if (d.on_fail && d.on_fail !== "refuse") out.on_fail = d.on_fail;
  if (d.refusal.trim()) out.refusal = d.refusal.trim();
  if (String(d.max_tool_calls).trim()) out.max_tool_calls = Number(d.max_tool_calls);
  return out;
};
const guardActive = (g) => !!(g && (g.scope || g.grounding === "tools" || g.verify || g.max_tool_calls));
const whenAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 90) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return new Date(iso).toLocaleString();
};

function Section({ icon: Icon, title, aside, children, testid }) {
  return (
    <section className={sectionCls} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-page-text m-0 flex items-center gap-2">
          {Icon && <Icon size={15} className="opacity-70" />} {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Fact({ label, children, mono }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-sm items-baseline">
      <span className="text-page-muted">{label}</span>
      <span className={"text-page-text min-w-0 break-words " + (mono ? "font-mono text-xs" : "")}>{children}</span>
    </div>
  );
}

// The parameters a tool takes, read off its JSON schema.
function schemaParams(schema) {
  const props = schema && typeof schema === "object" && schema.properties && typeof schema.properties === "object" ? schema.properties : {};
  const required = new Set(Array.isArray(schema?.required) ? schema.required : []);
  return Object.entries(props).map(([name, def]) => ({
    name, required: required.has(name),
    type: def && typeof def === "object" ? (Array.isArray(def.type) ? def.type.join("|") : def.type || (def.enum ? "enum" : "")) : "",
    description: def && typeof def === "object" && typeof def.description === "string" ? def.description : "",
  }));
}

export default function AgentPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { sharedSurfaceOnly } = useProject();
  const canBuild = !isPortal() && !sharedSurfaceOnly;

  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const EMPTY_TOOL = { endpoint: "", name: "", description: "", params: '{"type":"object","properties":{}}' };
  const [newTool, setNewTool] = useState(EMPTY_TOOL);
  const [toolError, setToolError] = useState(null);
  const [guardLog, setGuardLog] = useState(null); // { events, summary } — the guardrail ledger

  const load = () => {
    setLoading(true); setErr(null);
    getAgentConfig(agentId)
      .then((r) => setCfg(r))
      .catch((e) => setErr(e?.message || String(e)))
      .finally(() => setLoading(false));
    getAgentGuardrailEvents(agentId, 12).then(setGuardLog).catch(() => setGuardLog(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [agentId]);

  const agent = cfg?.agent || null;
  const manifest = cfg?.manifest || null;
  const runtime = cfg?.runtime || {};
  const models = cfg?.models || [];
  const modelLabel = (id) => (models.find((m) => m.id === id)?.label) || id || "—";
  const displayName = (manifest?.display_name) || agent?.display_name || agent?.name || "";
  const icon = (manifest?.icon) || agent?.icon || "🤖";
  const description = manifest?.description ?? agent?.description ?? "";

  const startEdit = () => {
    setDraft({
      display_name: displayName,
      icon: manifest?.icon || "",
      description: manifest?.description || "",
      persona_prompt: manifest?.persona_prompt || "",
      model: manifest?.model || "",
      guardrails: guardDraftFrom(manifest?.guardrails),
      // Tools are DATA up to the endpoint they name: reword, remove (undo before
      // saving), attach an endpoint that already exists on the fork.
      tools: (Array.isArray(manifest?.tools) ? manifest.tools : []).map((t) => ({
        ...JSON.parse(JSON.stringify(t)), removed: false, isNew: false,
      })),
    });
    setNewTool(EMPTY_TOOL); setToolError(null);
    setSaveErr(null);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft(null); setSaveErr(null); };
  const save = async () => {
    if (!draft) return;
    setSaving(true); setSaveErr(null);
    try {
      // Send only what changed — the backend refuses nothing here, but a
      // smaller patch reads better in its log.
      const patch = {};
      if (draft.display_name !== displayName) patch.display_name = draft.display_name;
      if (draft.icon !== (manifest?.icon || "")) patch.icon = draft.icon;
      if (draft.description !== (manifest?.description || "")) patch.description = draft.description;
      if (draft.persona_prompt !== (manifest?.persona_prompt || "")) patch.persona_prompt = draft.persona_prompt;
      if (draft.model !== (manifest?.model || "")) patch.model = draft.model;
      const guardNow = guardPatch(draft.guardrails || EMPTY_GUARD);
      if (JSON.stringify(guardNow) !== JSON.stringify(manifest?.guardrails || {})) patch.guardrails = guardNow;
      const toolsNow = draft.tools.filter((t) => !t.removed).map(({ name, description, endpoint, input_schema }) => ({ name, description, endpoint, input_schema }));
      if (JSON.stringify(toolsNow) !== JSON.stringify(manifest?.tools || [])) {
        if (toolsNow.length === 0) throw new Error("An agent needs at least one tool — the chat drops an agent without tools.");
        patch.tools = toolsNow;
      }
      if (Object.keys(patch).length) {
        const r = await updateAgentConfig(agentId, patch);
        setCfg((prev) => ({
          ...prev,
          manifest: r.manifest,
          runtime: r.runtime || prev.runtime,
          agent: { ...prev.agent, display_name: r.manifest.display_name, description: r.manifest.description, icon: r.manifest.icon },
        }));
      }
      setEditing(false); setDraft(null); setSavedAt(Date.now());
    } catch (e) {
      setSaveErr(e?.message || String(e));
    } finally { setSaving(false); }
  };

  const chat = () => {
    if (!agent) return;
    window.dispatchEvent(new CustomEvent("cortex:open-harness", {
      detail: {
        harness: "orchestrator",
        selectedAgent: { name: agent.name, display_name: displayName, icon: emojiOf(icon) },
      },
    }));
  };

  const toggleVisibility = async () => {
    if (!agent?.can_manage) return;
    const next = agent.visibility === "org" ? "private" : "org";
    try {
      await updateAgentVisibility(agent.id, next);
      setCfg((prev) => ({ ...prev, agent: { ...prev.agent, visibility: next } }));
    } catch (e) { setErr(e?.message || String(e)); }
  };

  const tools = useMemo(() => (Array.isArray(manifest?.tools) ? manifest.tools : []), [manifest]);

  const TOOL_NAME_RE = /^[a-z0-9_-]{1,64}$/;
  const defaultToolName = (endpoint) => {
    if (!endpoint) return "";
    const own = agent ? `cortex.agent.${agent.name}.` : null;
    const rest = own && endpoint.startsWith(own) ? endpoint.slice(own.length) : endpoint.split(".").pop();
    return rest.toLowerCase().replace(/[^a-z0-9_-]+/g, "_").slice(0, 64);
  };
  const updateTool = (i, fn) => setDraft((d) => ({ ...d, tools: d.tools.map((t, k) => (k === i ? fn({ ...t }) : t)) }));
  const setParamDescription = (i, param, value) => updateTool(i, (t) => {
    const schema = t.input_schema && typeof t.input_schema === "object" ? JSON.parse(JSON.stringify(t.input_schema)) : { type: "object", properties: {} };
    schema.properties = schema.properties || {};
    schema.properties[param] = { ...(schema.properties[param] || {}), description: value };
    return { ...t, input_schema: schema };
  });
  const addTool = () => {
    setToolError(null);
    const name = newTool.name.trim();
    if (!newTool.endpoint) return setToolError("Choose an endpoint.");
    if (!TOOL_NAME_RE.test(name)) return setToolError("The tool name must be lowercase letters, digits, _ or - (1–64 characters).");
    if (draft.tools.some((t) => t.name === name)) return setToolError(`'${name}' is already a tool of this agent.`);
    let input_schema;
    try {
      input_schema = JSON.parse(newTool.params || "{}");
      if (!input_schema || typeof input_schema !== "object" || Array.isArray(input_schema)) throw new Error("not an object");
    } catch { return setToolError("Parameters must be a JSON-schema object, e.g. {\"type\":\"object\",\"properties\":{}}."); }
    setDraft((d) => ({ ...d, tools: [...d.tools, { name, description: newTool.description.trim(), endpoint: newTool.endpoint, input_schema, removed: false, isNew: true }] }));
    setNewTool(EMPTY_TOOL);
  };
  const usedEndpoints = new Set((draft?.tools || []).map((t) => t.endpoint));

  const back = (
    <button type="button" onClick={() => navigate(createPageUrl("agents"))} className={btnGhost + " border-0 px-0 hover:bg-transparent text-page-muted hover:text-page-text"}>
      <ArrowLeft size={16} /> Back to Agents
    </button>
  );

  if (loading) {
    return (
      <div className="grid gap-4" data-testid="agent-page">
        {back}
        <div className="flex items-center gap-2 text-sm text-page-muted"><Loader2 size={16} className="animate-spin" /> Loading the agent…</div>
      </div>
    );
  }
  if (err || !agent) {
    return (
      <div className="grid gap-4" data-testid="agent-page">
        {back}
        <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 text-sm px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={15} className="shrink-0" /> {err || "Agent not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-5" data-testid="agent-page" data-agent-name={agent.name}>
      {back}

      {/* Header: identity + the two actions */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          {editing ? (
            <input
              data-testid="agent-field-icon"
              value={draft.icon}
              onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
              maxLength={24}
              className={fieldCls + " w-28 text-center"}
              title="Icon — an emoji, or a lucide icon name such as shield-check"
            />
          ) : (
            <AgentIcon icon={icon} size={40} className="text-page-text" />
          )}
          <div className="min-w-0 grid gap-1">
            {editing ? (
              <input
                data-testid="agent-field-display-name"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                className={fieldCls + " text-lg font-medium"}
                placeholder="Display name"
              />
            ) : (
              <h1 className="text-2xl md:text-[28px] font-medium leading-tight text-page-title m-0 break-words" data-testid="agent-title">{displayName}</h1>
            )}
            {editing ? (
              <textarea
                data-testid="agent-field-description"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={2}
                className={fieldCls}
                placeholder="What this agent does, in one or two sentences"
              />
            ) : (
              description ? <p className="text-sm text-page-muted m-0 max-w-3xl" data-testid="agent-description">{description}</p> : null
            )}
            <div className="flex flex-wrap items-center gap-3 text-xs text-page-muted mt-1">
              <span className="inline-flex items-center gap-1 font-mono">{agent.name}</span>
              <span className="inline-flex items-center gap-1">
                {agent.visibility === "org"
                  ? <><Globe size={12} /> Visible to the organization</>
                  : <><Lock size={12} /> Private</>}
              </span>
              {agent.shares?.length > 0 && <span className="inline-flex items-center gap-1"><Users size={12} /> Shared with {agent.shares.length}</span>}
              {agent.owner?.email && <span>Owner: {agent.owner.name || agent.owner.email}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {editing ? (
            <>
              <button type="button" data-testid="agent-cancel" className={btnGhost} onClick={cancelEdit} disabled={saving}><X size={14} /> Cancel</button>
              <button type="button" data-testid="agent-save" className={btnPrimary} onClick={save} disabled={saving}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </>
          ) : (
            <>
              {cfg.editable && canBuild && (
                <button type="button" data-testid="agent-edit" className={btnGhost} onClick={startEdit}><Pencil size={14} /> Edit</button>
              )}
              <button type="button" data-testid="agent-chat" className={btnPrimary} onClick={chat}>
                <MessageSquare size={14} /> Chat with agent
              </button>
            </>
          )}
        </div>
      </div>

      {saveErr && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 text-sm px-3 py-2 flex items-center gap-2" data-testid="agent-save-error">
          <AlertTriangle size={15} className="shrink-0" /> {saveErr}
        </div>
      )}
      {savedAt && !editing && (
        <div className="text-xs text-page-muted" data-testid="agent-saved">Saved. The orchestrator reads the new configuration on its next discovery (within a minute).</div>
      )}
      {cfg.run_error && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={15} className="shrink-0" /> The manifest endpoint did not run ({cfg.run_error}); showing the stored configuration.
        </div>
      )}
      {!cfg.static_manifest && manifest && (
        <div className="rounded-md border border-card-border bg-card-bg text-page-muted text-sm px-3 py-2 flex items-center gap-2">
          <Wrench size={15} className="shrink-0" /> This agent's manifest is computed in Flow ({cfg.manifest_endpoint}); its configuration is read-only here and edited in the Flow studio.
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[2fr_1fr] items-start">
        {/* Left: persona, tools, actor */}
        <div className="grid gap-5 min-w-0">
          <Section icon={ShieldCheck} title="Persona" testid="agent-section-persona"
            aside={<span className="text-xs text-page-muted">The instructions the agent runs with</span>}>
            {editing ? (
              <textarea
                data-testid="agent-field-persona"
                value={draft.persona_prompt}
                onChange={(e) => setDraft({ ...draft, persona_prompt: e.target.value })}
                rows={14}
                className={fieldCls + " font-mono text-xs leading-relaxed"}
              />
            ) : (
              <pre className="m-0 whitespace-pre-wrap break-words text-xs leading-relaxed text-page-text max-h-[420px] overflow-auto font-sans" data-testid="agent-persona">
                {manifest?.persona_prompt || "—"}
              </pre>
            )}
          </Section>

          <Section icon={Wrench} title={`Tools (${editing ? draft.tools.filter((t) => !t.removed).length : tools.length})`} testid="agent-section-tools"
            aside={<span className="text-xs text-page-muted">{editing ? "Reword, remove, or attach an endpoint that already exists on this project" : "Each tool is an endpoint on this project's engine"}</span>}>
            {editing ? (
              <div className="grid gap-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-xs text-page-muted">
                        <th className="py-1.5 pr-3 font-medium">Tool</th>
                        <th className="py-1.5 pr-3 font-medium w-[34%]">What it does</th>
                        <th className="py-1.5 pr-3 font-medium w-[30%]">Parameters</th>
                        <th className="py-1.5 pr-3 font-medium">Endpoint</th>
                        <th className="py-1.5 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.tools.map((t, i) => {
                        const params = schemaParams(t.input_schema);
                        return (
                          <tr key={t.name} className={"border-t border-card-border align-top " + (t.removed ? "opacity-50" : "")} data-testid={`agent-tool-${t.name}`}>
                            <td className={"py-2 pr-3 font-mono text-xs whitespace-nowrap " + (t.removed ? "line-through" : "")}>
                              {t.name}{t.isNew && <span className="ml-1 rounded-full bg-nav-active/15 text-nav-active px-1.5 py-0.5 text-[10px] font-sans no-underline">new</span>}
                            </td>
                            <td className="py-2 pr-3">
                              <input data-testid={`agent-tool-desc-${t.name}`} value={t.description || ""} disabled={t.removed}
                                onChange={(e) => updateTool(i, (x) => ({ ...x, description: e.target.value }))}
                                className={fieldCls} placeholder="What it does — the agent reads this to decide when to call it" />
                            </td>
                            <td className="py-2 pr-3">
                              {params.length === 0 ? <span className="text-page-muted text-xs">none</span> : (
                                <div className="grid gap-1.5">
                                  {params.map((p) => (
                                    <label key={p.name} className="grid gap-0.5">
                                      <span className="font-mono text-[11px] text-page-muted">{p.name}{p.required ? "*" : ""}{p.type ? `:${p.type}` : ""}</span>
                                      <input data-testid={`agent-tool-param-${t.name}-${p.name}`} value={p.description || ""} disabled={t.removed}
                                        onChange={(e) => setParamDescription(i, p.name, e.target.value)} className={fieldCls + " text-xs"} placeholder="what this parameter means" />
                                    </label>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-2 pr-3 font-mono text-xs break-all text-page-muted">{t.endpoint}</td>
                            <td className="py-2 whitespace-nowrap">
                              <button type="button" data-testid={`agent-tool-remove-${t.name}`} className={btnGhost + " text-xs"}
                                onClick={() => updateTool(i, (x) => ({ ...x, removed: !x.removed }))}>
                                {t.removed ? "Undo" : "Remove"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="rounded-md border border-dashed border-card-border p-3 grid gap-2" data-testid="agent-tool-add-row">
                  <div className="text-xs font-medium text-page-text">Attach an existing endpoint as a tool</div>
                  <div className="grid gap-2 md:grid-cols-[2fr_1fr]">
                    <select data-testid="agent-tool-add-endpoint" className={fieldCls} value={newTool.endpoint}
                      onChange={(e) => setNewTool((n) => ({ ...n, endpoint: e.target.value, name: n.name || defaultToolName(e.target.value) }))}>
                      <option value="">Choose an endpoint on this project…</option>
                      {[...new Set(cfg.fork_endpoints || [])].map((ep) => (
                        <option key={ep} value={ep}>{ep}{usedEndpoints.has(ep) ? " (already a tool)" : ""}</option>
                      ))}
                    </select>
                    <input data-testid="agent-tool-add-name" className={fieldCls + " font-mono text-xs"} placeholder="tool name (a-z 0-9 _ -)"
                      value={newTool.name} onChange={(e) => setNewTool((n) => ({ ...n, name: e.target.value }))} />
                  </div>
                  <input data-testid="agent-tool-add-desc" className={fieldCls} placeholder="What it does — the agent reads this to decide when to call it"
                    value={newTool.description} onChange={(e) => setNewTool((n) => ({ ...n, description: e.target.value }))} />
                  <textarea data-testid="agent-tool-add-params" rows={2} className={fieldCls + " font-mono text-xs"} title="Parameters, as a JSON schema"
                    value={newTool.params} onChange={(e) => setNewTool((n) => ({ ...n, params: e.target.value }))} />
                  {toolError && <div className="text-xs text-red-500" data-testid="agent-tool-add-error">{toolError}</div>}
                  <button type="button" data-testid="agent-tool-add" className={btnGhost + " justify-self-start"} onClick={addTool} disabled={!newTool.endpoint}>
                    + Add tool
                  </button>
                  {(cfg.fork_endpoints || []).length === 0 && <div className="text-xs text-page-muted">The fork's endpoint list is not available right now.</div>}
                </div>
                <div className="text-xs text-page-muted">A tool that needs NEW code is a change for the fleet — "Build a change" on the Agents page files it.</div>
              </div>
            ) : tools.length === 0 ? (
              <div className="text-sm text-page-muted">This agent has no tools — it answers from its persona alone.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-xs text-page-muted">
                      <th className="py-1.5 pr-3 font-medium">Tool</th>
                      <th className="py-1.5 pr-3 font-medium">What it does</th>
                      <th className="py-1.5 pr-3 font-medium">Parameters</th>
                      <th className="py-1.5 font-medium">Endpoint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tools.map((t) => {
                      const params = schemaParams(t.input_schema);
                      return (
                        <tr key={t.name} className="border-t border-card-border align-top" data-testid={`agent-tool-${t.name}`}>
                          <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{t.name}</td>
                          <td className="py-2 pr-3 text-page-text">{t.description || <span className="text-page-muted">—</span>}</td>
                          <td className="py-2 pr-3">
                            {params.length === 0 ? <span className="text-page-muted text-xs">none</span> : (
                              <div className="flex flex-wrap gap-1">
                                {params.map((p) => (
                                  <span key={p.name} title={p.description} className="inline-flex items-center gap-1 rounded-full border border-card-border px-2 py-0.5 text-xs font-mono">
                                    {p.name}{p.required ? "*" : ""}{p.type ? <span className="text-page-muted">:{p.type}</span> : null}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-2 font-mono text-xs break-all text-page-muted">{t.endpoint}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section icon={ShieldAlert} title="Safeguards" testid="agent-section-guardrails"
            aside={<span className="text-xs text-page-muted">{editing ? "What makes an answer acceptable — enforced by the platform after every run" : (guardActive(manifest?.guardrails) ? "Enforced around every answer" : "None — the persona alone decides")}</span>}>
            {editing ? (
              <div className="grid gap-3" data-testid="agent-guardrails-editor">
                <label className="grid gap-1 text-xs text-page-muted">Scope — what this agent may answer (anything else is refused)
                  <textarea data-testid="agent-guard-scope" rows={3} className={fieldCls + " text-sm"} value={draft.guardrails.scope}
                    onChange={(e) => setDraft({ ...draft, guardrails: { ...draft.guardrails, scope: e.target.value } })}
                    placeholder="e.g. Questions about this project's data quality results. Nothing about other projects, pricing or legal advice." />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1 text-xs text-page-muted">Grounding
                    <select data-testid="agent-guard-grounding" className={fieldCls + " cursor-pointer"} value={draft.guardrails.grounding}
                      onChange={(e) => setDraft({ ...draft, guardrails: { ...draft.guardrails, grounding: e.target.value } })}>
                      <option value="none">Persona decides</option>
                      <option value="tools">Tools only — every fact must come from a tool result of the run</option>
                    </select>
                  </label>
                  <label className="grid gap-1 text-xs text-page-muted">When a check fails
                    <select data-testid="agent-guard-on-fail" className={fieldCls + " cursor-pointer"} value={draft.guardrails.on_fail}
                      onChange={(e) => setDraft({ ...draft, guardrails: { ...draft.guardrails, on_fail: e.target.value } })}>
                      <option value="refuse">Refuse — the user never sees the unverified draft</option>
                      <option value="annotate">Annotate — deliver the draft under a visible warning</option>
                    </select>
                  </label>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-page-text cursor-pointer">
                  <input type="checkbox" data-testid="agent-guard-verify" checked={draft.guardrails.verify}
                    onChange={(e) => setDraft({ ...draft, guardrails: { ...draft.guardrails, verify: e.target.checked } })} />
                  Verify every answer — a second, tool-less model pass checks each claim against the tool evidence before the user sees it
                </label>
                <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
                  <label className="grid gap-1 text-xs text-page-muted">Refusal text (shown instead of a refused answer)
                    <input data-testid="agent-guard-refusal" className={fieldCls} value={draft.guardrails.refusal}
                      onChange={(e) => setDraft({ ...draft, guardrails: { ...draft.guardrails, refusal: e.target.value } })}
                      placeholder="I cannot give a verified answer to that from the data available to me." />
                  </label>
                  <label className="grid gap-1 text-xs text-page-muted">Max tool calls per run
                    <input data-testid="agent-guard-max-tools" type="number" min={1} max={60} className={fieldCls} value={draft.guardrails.max_tool_calls}
                      onChange={(e) => setDraft({ ...draft, guardrails: { ...draft.guardrails, max_tool_calls: e.target.value } })} placeholder="no cap" />
                  </label>
                </div>
              </div>
            ) : guardActive(manifest?.guardrails) ? (
              <div className="grid gap-1.5" data-testid="agent-guardrails">
                {manifest.guardrails.scope && <Fact label="Scope">{manifest.guardrails.scope}</Fact>}
                <Fact label="Grounding">{manifest.guardrails.grounding === "tools" ? "Tools only — an answer with no successful tool call behind it is refused" : "Persona decides"}</Fact>
                <Fact label="Verification">{manifest.guardrails.verify ? "Every answer is checked by a second model pass against the tool evidence" : "Off"}</Fact>
                <Fact label="On failure">{manifest.guardrails.on_fail === "annotate" ? "The draft is delivered under a visible warning" : `The user sees the refusal: “${manifest.guardrails.refusal || "I cannot give a verified answer to that from the data available to me."}”`}</Fact>
                {manifest.guardrails.max_tool_calls && <Fact label="Tool budget">{manifest.guardrails.max_tool_calls} calls per run; exceeding it ends the run as an error</Fact>}
              </div>
            ) : (
              <div className="text-sm text-page-muted" data-testid="agent-guardrails">No safeguards. Add a scope, tools-only grounding or verification to have the platform check every answer before the user sees it.</div>
            )}
            {guardLog && guardLog.summary?.total > 0 && (
              <div className="grid gap-1.5 border-t border-card-border pt-3" data-testid="agent-guardrail-log">
                <div className="text-xs text-page-muted">
                  Ledger: {guardLog.summary.total} guarded answer{guardLog.summary.total === 1 ? "" : "s"} · {guardLog.summary.passed} passed · {guardLog.summary.failed} failed · {guardLog.summary.refused} refused
                </div>
                <ul className="m-0 p-0 list-none grid gap-1">
                  {guardLog.events.slice(0, 8).map((ev) => (
                    <li key={ev.id} className="text-xs flex flex-wrap items-baseline gap-x-2" data-testid="agent-guardrail-event" data-verdict={ev.verdict}>
                      <span className={"font-medium " + (ev.verdict === "pass" ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400")}>{ev.verdict === "pass" ? "✓ pass" : (ev.refused ? "⛔ refused" : "⚠ failed")}</span>
                      <span className="text-page-muted">{whenAgo(ev.created_at)} · {ev.tool_calls} tool call{ev.tool_calls === 1 ? "" : "s"}{ev.user_email ? ` · ${ev.user_email}` : ""}</span>
                      <span className="text-page-text truncate max-w-full" title={ev.task || ""}>{(ev.task || "").slice(0, 120)}</span>
                      {ev.reason && <span className="text-page-muted basis-full">{ev.reason}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {runtime.is_actor && (
            <Section icon={Workflow} title="Workflow actor" testid="agent-section-actor"
              aside={<span className="text-xs text-page-muted">This agent can be assigned process steps</span>}>
              <div className="text-sm text-page-text">
                Workflows can assign steps to this agent; the actor worker executes them from this manifest — the persona above is its execution contract.
              </div>
              <pre className="m-0 whitespace-pre-wrap break-words text-xs text-page-muted bg-page-bg/40 rounded-md p-2 overflow-auto max-h-60">{JSON.stringify(runtime.actor, null, 2)}</pre>
            </Section>
          )}
        </div>

        {/* Right: model, works over, how it runs, access */}
        <div className="grid gap-5 min-w-0">
          <Section icon={Cpu} title="Model" testid="agent-section-model">
            {editing ? (
              <select
                data-testid="agent-field-model"
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                className={fieldCls}
              >
                <option value="">Platform default{runtime.default_model ? ` (${modelLabel(runtime.default_model)})` : ""}</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}{m.vendor ? ` · ${m.vendor}` : ""}</option>
                ))}
              </select>
            ) : (
              <>
                <div className="text-base text-page-text" data-testid="agent-model-effective">{modelLabel(runtime.effective_model)}</div>
                <div className="text-xs text-page-muted">
                  {runtime.model
                    ? <>Set on this agent. The platform default is {modelLabel(runtime.default_model)}.</>
                    : <>The platform default for project agents. Choose a model here to run this agent on a different one.</>}
                </div>
              </>
            )}
          </Section>

          <Section icon={AppWindow} title="Works over" testid="agent-section-works-over">
            {(agent.apps?.length || 0) + (agent.sources?.length || 0) === 0 ? (
              <div className="text-sm text-page-muted">Not attached to an app or a data source — it works over the project through its tools.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(agent.apps || []).map((a) => (
                  <button key={`app-${a.id}`} type="button" onClick={() => navigate(createPageUrl(`apps/${encodeURIComponent(a.view_name)}`))}
                    className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2 py-0.5 cursor-pointer hover:bg-card-border/30">
                    <AppWindow size={11} className="opacity-70" /> {a.title || a.view_name}
                  </button>
                ))}
                {(agent.sources || []).map((s) => (
                  <button key={`src-${s.source_id}`} type="button" onClick={() => navigate(createPageUrl(`data-sources/${encodeURIComponent(s.source_id)}`))}
                    className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2 py-0.5 cursor-pointer hover:bg-card-border/30">
                    <Box size={11} className="opacity-70" /> {s.source_id}
                  </button>
                ))}
              </div>
            )}
          </Section>

          <Section icon={Route} title="How it runs" testid="agent-section-runtime">
            <Fact label="In chat">The project's Cortex chat hands it work through <span className="font-mono text-xs">{runtime.delegate_tool}</span>; "Chat with agent" pins it.</Fact>
            <Fact label="In workflows">{runtime.is_actor ? "Assignable as an actor to process steps." : "Not a workflow actor."}</Fact>
            <Fact label="Manifest" mono>{cfg.manifest_endpoint}</Fact>
            {runtime.stamp_version != null && <Fact label="Stamp">v{String(runtime.stamp_version)} (platform-stamped agent)</Fact>}
            <Fact label="Editable">{cfg.static_manifest ? "Identity, persona, model, tools and safeguards from this page." : "Only in the Flow studio (computed manifest)."}</Fact>
          </Section>

          <Section icon={Users} title="Access" testid="agent-section-access"
            aside={agent.can_manage && (
              <button type="button" className={btnGhost + " text-xs"} onClick={toggleVisibility} data-testid="agent-visibility-toggle">
                {agent.visibility === "org" ? <><Lock size={12} /> Make private</> : <><Globe size={12} /> Make org-visible</>}
              </button>
            )}>
            <Fact label="Visibility">{agent.visibility === "org" ? "Everyone in the organization" : "Owner and the people it is shared with"}</Fact>
            <Fact label="Owner">{agent.owner ? (agent.owner.name || agent.owner.email) : "—"}</Fact>
            {agent.can_manage && (
              <Fact label="Shared with">{agent.shares?.length ? agent.shares.map((s) => s.name || s.email).join(", ") : "nobody yet — share it from the Agents page"}</Fact>
            )}
          </Section>

          <button type="button" className={btnGhost + " text-xs justify-self-start"} onClick={load}><RefreshCw size={12} /> Reload</button>
        </div>
      </div>
    </div>
  );
}
